import { Plugin } from 'vite';
import * as path from 'path';
import fs from 'fs-extra';

export interface DevToolsConfig {
	hmr?: boolean;
	overlay?: boolean;
	eslint?: boolean;
	prettier?: boolean;
	testing?: {
		framework?: 'vitest' | 'jest';
		coverage?: boolean;
		ui?: boolean;
	};
	performance?: {
		bundleAnalyzer?: boolean;
		lighthouse?: boolean;
	};
}

export function createDevToolsPlugin(cwd: string, config: DevToolsConfig = {}): Plugin {
	// Helper functions
	const configureHMR = (server: any, config: DevToolsConfig) => {
		// Enhanced HMR for Vue components
		server.ws.on('frame:component-update', (data: any) => {
			console.log(`[Frame HMR] Component updated: ${data.file}`);
		});

		// Route-based HMR
		server.ws.on('frame:route-update', (data: any) => {
			console.log(`[Frame HMR] Route updated: ${data.route}`);
			server.ws.send({
				type: 'full-reload',
				path: '*'
			});
		});
	};

	const createDevMiddleware = (cwd: string, config: DevToolsConfig) => {
		return (req: any, res: any, next: any) => {
			if (req.url === '/frame-dev/status') {
				res.setHeader('Content-Type', 'application/json');
				res.end(JSON.stringify({
					status: 'active',
					features: {
						hmr: config.hmr !== false,
						eslint: config.eslint === true,
						testing: !!config.testing,
						performance: !!config.performance
					},
					timestamp: new Date().toISOString()
				}));
				return;
			}

			if (req.url === '/frame-dev/health') {
				res.setHeader('Content-Type', 'application/json');
				res.end(JSON.stringify({
					healthy: true,
					uptime: process.uptime(),
					memory: process.memoryUsage(),
					version: '0.1.0'
				}));
				return;
			}

			next();
		};
	};

	const addDevOptimizations = (config: any, devConfig: DevToolsConfig) => {
		// Add performance monitoring
		if (devConfig.performance?.bundleAnalyzer) {
			config.build = config.build || {};
			config.build.rollupOptions = config.build.rollupOptions || {};
			config.build.rollupOptions.plugins = config.build.rollupOptions.plugins || [];
			// Bundle analyzer would be added here
		}

		// Enhanced error overlay
		if (devConfig.overlay !== false) {
			config.server = config.server || {};
			config.server.hmr = config.server.hmr || {};
			config.server.hmr.overlay = true;
		}
	};

	const generateDevConfigs = async (cwd: string, config: DevToolsConfig) => {
		const devDir = path.join(cwd, '.frame-dev');
		await fs.ensureDir(devDir);

		// Generate ESLint config if enabled
		if (config.eslint) {
			await generateESLintConfig(cwd);
		}

		// Generate Prettier config if enabled
		if (config.prettier) {
			await generatePrettierConfig(cwd);
		}

		// Generate test configuration if enabled
		if (config.testing) {
			await generateTestConfig(cwd, config.testing);
		}

		// Generate development utilities
		await generateDevUtils(devDir);
	};

	const generateESLintConfig = async (cwd: string) => {
		const eslintConfig = {
			env: {
				browser: true,
				es2021: true,
				node: true
			},
			extends: [
				'eslint:recommended',
				'@typescript-eslint/recommended',
				'plugin:vue/vue3-essential'
			],
			overrides: [],
			parser: 'vue-eslint-parser',
			parserOptions: {
				ecmaVersion: 'latest',
				parser: '@typescript-eslint/parser',
				sourceType: 'module'
			},
			plugins: [
				'vue',
				'@typescript-eslint'
			],
			rules: {
				'vue/multi-word-component-names': 'off',
				'@typescript-eslint/no-unused-vars': 'warn',
				'@typescript-eslint/no-explicit-any': 'warn'
			},
			ignorePatterns: [
				'dist/',
				'node_modules/',
				'.frame/'
			]
		};

		const configPath = path.join(cwd, '.eslintrc.json');
		await fs.writeFile(configPath, JSON.stringify(eslintConfig, null, 2));
		console.log('[Frame DevTools] Generated .eslintrc.json');
	};

	const generatePrettierConfig = async (cwd: string) => {
		const prettierConfig = {
			semi: true,
			trailingComma: 'es5',
			singleQuote: true,
			printWidth: 100,
			tabWidth: 2,
			useTabs: false,
			endOfLine: 'lf'
		};

		const configPath = path.join(cwd, '.prettierrc.json');
		await fs.writeFile(configPath, JSON.stringify(prettierConfig, null, 2));

		// Generate .prettierignore
		const prettierIgnore = `
node_modules/
dist/
.frame/
*.min.js
*.min.css
`.trim();

		await fs.writeFile(path.join(cwd, '.prettierignore'), prettierIgnore);
		console.log('[Frame DevTools] Generated Prettier configuration');
	};

	const generateTestConfig = async (cwd: string, testConfig: any) => {
		if (testConfig.framework === 'vitest' || !testConfig.framework) {
			await generateVitestConfig(cwd, testConfig);
		} else if (testConfig.framework === 'jest') {
			await generateJestConfig(cwd, testConfig);
		}
	};

	const generateVitestConfig = async (cwd: string, testConfig: any) => {
		const vitestConfig = {
			test: {
				globals: true,
				environment: 'jsdom',
				coverage: testConfig.coverage ? {
					provider: 'v8',
					reporter: ['text', 'json', 'html']
				} : undefined
			}
		};

		const configPath = path.join(cwd, 'vitest.config.ts');
		const configContent = `import { defineConfig } from 'vitest/config'
import { createViteConfig } from '@livx.cc/frame'

export default defineConfig(async () => {
  const baseConfig = await createViteConfig(process.cwd())
  
  return {
    ...baseConfig,
    test: ${JSON.stringify(vitestConfig.test, null, 6)}
  }
})
`;

		await fs.writeFile(configPath, configContent);
		console.log('[Frame DevTools] Generated Vitest configuration');
	};

	const generateJestConfig = async (cwd: string, testConfig: any) => {
		const jestConfig = {
			preset: 'ts-jest',
			testEnvironment: 'jsdom',
			moduleFileExtensions: ['js', 'ts', 'vue'],
			transform: {
				'^.+\\.vue$': '@vue/vue3-jest',
				'^.+\\.ts$': 'ts-jest'
			},
			testMatch: [
				'**/tests/**/*.spec.(js|ts)',
				'**/__tests__/*.(js|ts)',
				'**/src/**/*.test.(js|ts)'
			],
			collectCoverage: testConfig.coverage || false,
			collectCoverageFrom: [
				'src/**/*.{js,ts,vue}',
				'!src/**/*.d.ts'
			]
		};

		const configPath = path.join(cwd, 'jest.config.js');
		const configContent = `module.exports = ${JSON.stringify(jestConfig, null, 2)}`;

		await fs.writeFile(configPath, configContent);
		console.log('[Frame DevTools] Generated Jest configuration');
	};

	const generateDevUtils = async (devDir: string) => {
		// Generate development utilities and helpers
		const devUtilsContent = `// Frame Development Utilities
// Auto-generated - do not edit manually

export interface DevInfo {
  buildTime: string;
  environment: string;
  features: string[];
}

export function getDevInfo(): DevInfo {
  return {
    buildTime: new Date().toISOString(),
    environment: import.meta.env.MODE || 'development',
    features: ['hmr', 'typescript', 'routing', 'assets']
  };
}

export function logPerformance(label: string, fn: () => void) {
  const start = performance.now();
  fn();
  const end = performance.now();
  console.log(\`[Frame Performance] \${label}: \${(end - start).toFixed(2)}ms\`);
}

export function logComponentUpdate(component: string) {
  console.log(\`[Frame HMR] \${component} updated at \${new Date().toLocaleTimeString()}\`);
}
`;

		await fs.writeFile(path.join(devDir, 'utils.ts'), devUtilsContent);
	};

	// Return the plugin object
	return {
		name: 'frame-dev-tools',

		async configureServer(server) {
			// Enhanced HMR configuration
			if (config.hmr !== false) {
				configureHMR(server, config);
			}

			// Development middleware
			server.middlewares.use('/frame-dev', createDevMiddleware(cwd, config));

			console.log('[Frame DevTools] Development tools initialized');
		},

		configResolved(resolvedConfig) {
			// Add development-specific optimizations
			if (resolvedConfig.command === 'serve') {
				addDevOptimizations(resolvedConfig, config);
			}
		},

		buildStart() {
			// Generate development configuration files
			generateDevConfigs(cwd, config);
		},
	};
} 